// src/components/PersoneelDossierPopup.js
// vPDOS-005
//
// Centraal, read-only personeelsdossier.
// Toont alle direct aan het personeelslid gekoppelde gegevens en opent
// de bestaande bronprogramma's met de gekozen registratie geselecteerd.

import React, { useEffect, useState } from "react";

const VERSION = "vPDOS-005";

function safeText(value, fallback = "") {
  if (value === null || value === undefined) return fallback;
  return String(value);
}

function safeTrim(value) {
  return safeText(value).trim();
}

function isoDate(value) {
  const text = safeText(value).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : "";
}

function nlDate(value) {
  const date = isoDate(value);
  if (!date) return "";
  const [year, month, day] = date.split("-");
  return `${day}-${month}-${year}`;
}

function nlDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return nlDate(value) || safeText(value);
  return date.toLocaleString("nl-NL", {
    year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
  });
}

function decimalNl(value, decimals = 2) {
  if (value === null || value === undefined || value === "") return "-";
  const number = Number(String(value).replace(",", "."));
  if (!Number.isFinite(number)) return safeText(value) || "-";
  return number.toLocaleString("nl-NL", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function personName(row = {}) {
  return safeTrim(
    row.search_name ||
    row.full_name ||
    [row.first_name, row.name_prefix, row.last_name].map(safeTrim).filter(Boolean).join(" ")
  );
}

function noteType(row = {}) {
  return safeText(row.kind || row.type).toLowerCase() === "task" ? "Taak" : "Notitie";
}

function noteStatus(row = {}) {
  const value = safeText(row.status).toLowerCase();
  if (value === "done") return "Afgerond";
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : "-";
}

function availabilityLabel(value) {
  const type = safeText(value).toLowerCase();
  if (type === "afwezig") return "Afwezig";
  if (type === "vakantie") return "Vakantie";
  if (type === "ziek") return "Ziek";
  if (type === "verlof") return "Verlof";
  if (type === "niet_beschikbaar") return "Niet beschikbaar";
  return "Beschikbaar";
}

function buildHeaders() {
  let user = {};
  try {
    user = JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "{}");
  } catch {}

  const headers = {
    "Content-Type": "application/json",
    "x-tenant": sessionStorage.getItem("tenant") || localStorage.getItem("tenant") || "demo",
    "x-role": user?.role || "user",
    "x-staffiq-user-id": user?.id || "",
    "x-staffiq-user-email": user?.email || "",
    "x-staffiq-user-name": user?.display_name || user?.name || "",
    "x-company-code-ids": sessionStorage.getItem("staffiq_company_code_ids") || localStorage.getItem("staffiq_company_code_ids") || "",
  };

  const token = sessionStorage.getItem("token") || localStorage.getItem("token") || "";
  if (token) headers.Authorization = `Bearer ${token}`;
  Object.keys(headers).forEach((key) => { if (!headers[key]) delete headers[key]; });
  return headers;
}

async function fetchJson(url) {
  const response = await fetch(url, { headers: buildHeaders() });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data?.error || `${response.status} ${response.statusText}`);
  return data;
}

function storeAndOpen(key, value, path) {
  try {
    if (key && value !== null && value !== undefined && value !== "") {
      sessionStorage.setItem(key, String(value));
    }
  } catch {}
  window.location.href = path;
}

function openFiltered(path, personnelId) {
  const separator = path.includes("?") ? "&" : "?";
  window.location.href = `${path}${separator}personnel_id=${encodeURIComponent(personnelId)}`;
}

function CommunicationValue({ type, value }) {
  const clean = safeTrim(value);
  if (!clean || clean === "-") return <span>-</span>;

  function onClick() {
    if (type === "email") window.location.href = `mailto:${encodeURIComponent(clean)}`;
    else if (type === "mobile") {
      const choice = window.prompt(`Wat wil je doen met ${clean}?\n\n1 = Bellen\n2 = SMS\n3 = WhatsApp`, "3");
      if (choice === null) return;
      const selected = safeTrim(choice).toLowerCase();
      if (selected === "1" || selected === "bellen") window.location.href = `tel:${clean}`;
      else if (selected === "2" || selected === "sms") window.location.href = `sms:${clean}`;
      else if (selected === "3" || selected === "whatsapp" || selected === "app") {
        let number = clean.replace(/[^\d+]/g, "").replace(/^00/, "+");
        if (number.startsWith("0")) number = `+31${number.slice(1)}`;
        window.open(`https://wa.me/${number.replace(/[^\d]/g, "")}`, "_blank", "noopener,noreferrer");
      }
    } else window.location.href = `tel:${clean}`;
  }

  return (
    <button type="button" onClick={onClick} style={{ border: "none", background: "transparent", color: "#1f4e79", textDecoration: "underline", padding: 0, cursor: "pointer", font: "inherit", textAlign: "left" }}>
      {clean}
    </button>
  );
}

function Section({ title, count, children, onOpenAll, openAllLabel = "Alles openen", initiallyOpen = false }) {
  const [open, setOpen] = useState(initiallyOpen);
  return (
    <section style={{ border: "1px solid #d9e2ec", borderRadius: 8, background: "white", overflow: "hidden" }}>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto auto", gap: 8, alignItems: "center", padding: "10px 12px", background: "#f7fafc", borderBottom: open ? "1px solid #d9e2ec" : "none" }}>
        <button className="section-toggle" type="button" onClick={() => setOpen((v) => !v)} style={{ border: "none", background: "transparent", padding: 0, textAlign: "left", cursor: "pointer", font: "inherit", fontWeight: 700, color: "#1f4e79", minWidth: 0 }}>
          {open ? "▼" : "▶"} {title}
        </button>
        <span style={{ minWidth: 34, textAlign: "center", padding: "3px 7px", borderRadius: 999, background: "#e6f0ff", color: "#1f4e79", fontWeight: 700, fontSize: 12 }}>{Number(count || 0)}</span>
        {onOpenAll ? <button type="button" onClick={onOpenAll} style={{ padding: "6px 9px", border: "1px solid #d9d9d9", borderRadius: 5, background: "white", cursor: "pointer", whiteSpace: "nowrap" }}>{openAllLabel}</button> : <span />}
      </div>
      {open && <div style={{ padding: 12 }}>{children}</div>}
    </section>
  );
}

function EmptyText() { return <div style={{ color: "#777", fontSize: 13 }}>Geen gegevens gevonden.</div>; }
function TableWrap({ children, minWidth = 760 }) { return <div className="print-table-wrap" style={{ overflowX: "auto", border: "1px solid #eceff2", borderRadius: 6 }}><div className="print-table-inner" style={{ minWidth }}>{children}</div></div>; }

const tableStyle = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
const thStyle = { textAlign: "left", padding: "7px 8px", borderBottom: "2px solid #d9e2ec", background: "#fafafa", whiteSpace: "nowrap" };
const tdStyle = { padding: "7px 8px", borderBottom: "1px solid #eceff2", verticalAlign: "top" };
const linkButtonStyle = { border: "none", background: "transparent", color: "#1f4e79", textDecoration: "underline", padding: 0, cursor: "pointer", font: "inherit", whiteSpace: "nowrap" };

export default function PersoneelDossierPopup({ personnelId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const isMobile = window.innerWidth <= 768;

  async function loadDossier() {
    try {
      setLoading(true); setError("");
      const dossier = await fetchJson(`/api/personnel/${encodeURIComponent(personnelId)}/dossier`);
      setData(dossier && typeof dossier === "object" ? dossier : {});
    } catch (err) {
      setError(err?.message || "Dossier laden mislukt."); setData(null);
    } finally { setLoading(false); }
  }

  useEffect(() => { loadDossier(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [personnelId]);

  const person = data?.person || {};
  const matching = Array.isArray(data?.matching) ? data.matching : [];
  const notesTasks = Array.isArray(data?.notes_tasks) ? data.notes_tasks : [];
  const placements = Array.isArray(data?.placements) ? data.placements : [];
  const availability = Array.isArray(data?.availability) ? data.availability : [];
  const documents = Array.isArray(data?.documents) ? data.documents : [];
  const title = personName(person) || `Personeel ${personnelId}`;

  function printDossier() {
    function escapeHtml(value) {
      return safeText(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    }

    function yesNo(value) {
      return value === true ? "Ja" : value === false ? "Nee" : "-";
    }

    function printRow(label, value) {
      return `
        <tr>
          <td class="label">${escapeHtml(label)}</td>
          <td>${escapeHtml(safeTrim(value) || "-")}</td>
        </tr>`;
    }

    function tableSection(sectionTitle, headers, rows, emptyText = "Geen gegevens gevonden.") {
      const headerHtml = headers
        .map((header) => `<th>${escapeHtml(header)}</th>`)
        .join("");

      const bodyHtml = rows.length
        ? rows.map((row) => `
            <tr>
              ${row.map((value) => `<td>${escapeHtml(safeTrim(value) || "-")}</td>`).join("")}
            </tr>`).join("")
        : `<tr><td colspan="${headers.length}" class="empty">${escapeHtml(emptyText)}</td></tr>`;

      return `
        <section class="print-section">
          <h2>${escapeHtml(sectionTitle)}</h2>
          <table class="data-table">
            <thead><tr>${headerHtml}</tr></thead>
            <tbody>${bodyHtml}</tbody>
          </table>
        </section>`;
    }

    const fullName = [
      person.first_name,
      person.name_prefix,
      person.last_name,
    ].map(safeTrim).filter(Boolean).join(" ");

    const visitAddress = [
      [person.visit_street, person.visit_house_number, person.visit_addition]
        .map(safeTrim).filter(Boolean).join(" "),
      [person.visit_postcode, person.visit_city]
        .map(safeTrim).filter(Boolean).join(" "),
      person.visit_province,
      person.visit_region || person.region,
      person.visit_country,
    ].map(safeTrim).filter(Boolean).join(" ");

    const idDocument = [
      person.id_type,
      person.id_number,
      person.id_expiry_date ? `geldig tot ${nlDate(person.id_expiry_date)}` : "",
    ].map(safeTrim).filter(Boolean).join(" - ");

    const personRows = [
      ["Zoeknaam", person.search_name || title],
      ["Naam", fullName || title],
      ["Status", person.status],
      ["Functie", person.job_functions],
      ["Geslacht", person.gender],
      ["Geboortedatum", nlDate(person.birth_date)],
      ["Nationaliteit", person.nationality],
      ["Telefoon", person.phone],
      ["Mobiel", person.mobile],
      ["E-mail", person.email],
      ["Website", person.website],
      ["Bezoekadres", visitAddress],
      ["Bedrijfscode", person.company_code_name || person.company_code],
      ["Rijbewijs", person.driving_license],
      ["Eigen vervoer", yesNo(person.own_transport)],
      ["In dienst", nlDate(person.employment_start_date || person.start_date)],
      ["Uit dienst", nlDate(person.employment_end_date || person.end_date)],
      ["Actief", person.active === false ? "Nee" : "Ja"],
      ["ID document", idDocument],
      ["Opmerking", person.remarks],
    ];

    const matchingRows = matching.map((row) => [
      row.category || row.type || row.kind,
      row.name || row.description,
      row.level,
      row.description && row.name ? row.description : "",
      [nlDate(row.date_from), nlDate(row.date_to)].filter(Boolean).join(" t/m "),
      row.diploma === true ? "Ja" : "Nee",
      row.employer,
    ]);

    const noteRows = notesTasks.map((row) => [
      noteType(row),
      noteStatus(row),
      `${nlDate(row.due_date) || "-"}${safeTrim(row.due_time) ? ` ${safeTrim(row.due_time).slice(0, 5)}` : ""}`,
      row.subject || row.title,
      row.organisation_name,
      row.assigned_user_name,
    ]);

    const placementRows = placements.map((row) => [
      nlDate(row.placement_date),
      row.placement_number || row.id,
      row.organisation_name,
      row.job_title,
      [nlDate(row.start_date), nlDate(row.end_date)].filter(Boolean).join(" t/m "),
      decimalNl(row.hours_per_week),
      row.status,
      row.candidate_status,
      row.customer_status,
    ]);

    const availabilityRows = availability.map((row) => [
      availabilityLabel(row.type),
      nlDate(row.start_date),
      nlDate(row.end_date),
      decimalNl(row.hours_per_day),
      row.note,
    ]);

    const documentRows = documents.map((row) => [
      nlDate(row.document_date) || nlDateTime(row.created_at),
      row.document_type,
      row.file_name,
      row.description,
      row.organisation_name,
    ]);

    const photoHtml = person.profile_photo_path
      ? `<img class="photo" src="/api/personnel/${encodeURIComponent(personnelId)}/photo" alt="Pasfoto" />`
      : "";

    const win = window.open("", "_blank", "width=900,height=800");
    if (!win) {
      alert("Printvenster kon niet worden geopend. Controleer popupblokkering.");
      return;
    }

    win.document.open();
    win.document.write(`<!doctype html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>Dossier ${escapeHtml(title)}</title>
  <style>
    @page {
      size: A4 portrait;
      margin: 14mm 13mm 15mm 13mm;
    }

    * { box-sizing: border-box; }

    html, body {
      margin: 0;
      padding: 0;
      background: #fff;
      color: #111;
      font-family: Arial, Helvetica, sans-serif;
      font-size: 10px;
    }

    h1 {
      margin: 0 0 3px;
      font-size: 20px;
      font-weight: 700;
    }

    .subtitle {
      margin: 0 0 18px;
      color: #555;
      font-size: 10px;
    }

    h2 {
      margin: 16px 0 6px;
      padding-bottom: 4px;
      border-bottom: 1px solid #b8c4cf;
      color: #1f4e79;
      font-size: 14px;
      break-after: avoid;
      page-break-after: avoid;
    }

    .person-header {
      display: grid;
      grid-template-columns: minmax(0, 1fr) 88px;
      gap: 14px;
      align-items: start;
    }

    .photo {
      width: 82px;
      height: 109px;
      object-fit: cover;
      border: 1px solid #bbb;
      border-radius: 4px;
    }

    table {
      width: 100%;
      border-collapse: collapse;
    }

    .person-table {
      table-layout: fixed;
    }

    .person-table .label {
      width: 155px;
      font-weight: 700;
    }

    th {
      padding: 4px 5px;
      border-bottom: 2px solid #333;
      text-align: left;
      vertical-align: bottom;
      white-space: normal;
      font-size: 9px;
    }

    td {
      padding: 4px 5px;
      border-bottom: 1px solid #ddd;
      vertical-align: top;
      white-space: pre-wrap;
      overflow-wrap: anywhere;
      font-size: 9px;
    }

    .print-section {
      margin-top: 12px;
    }

    .data-table {
      table-layout: fixed;
    }

    .data-table tr {
      break-inside: avoid;
      page-break-inside: avoid;
    }

    .empty {
      color: #666;
      font-style: italic;
    }

    thead {
      display: table-header-group;
    }

    img {
      print-color-adjust: exact;
      -webkit-print-color-adjust: exact;
    }
  </style>
</head>
<body>
  <div class="person-header">
    <div>
      <h1>Dossier Personeel</h1>
      <div class="subtitle">${escapeHtml(title)}</div>
      <h2>Personeelskaart</h2>
      <table class="person-table">
        <tbody>${personRows.map(([label, value]) => printRow(label, value)).join("")}</tbody>
      </table>
    </div>
    <div>${photoHtml}</div>
  </div>

  ${tableSection(
    "Matchinggegevens",
    ["Rubriek", "Naam", "Niveau", "Omschrijving", "Periode", "Diploma", "Werkgever"],
    matchingRows
  )}

  ${tableSection(
    "Notities / Taken",
    ["Soort", "Status", "Vervaldatum", "Titel", "Relatie", "Uitvoerder"],
    noteRows
  )}

  ${tableSection(
    "Plaatsingen",
    ["Datum", "Nummer", "Relatie", "Functie", "Periode", "Uren", "Status", "Kandidaat", "Klant"],
    placementRows
  )}

  ${tableSection(
    "Beschikbaarheid / Afwezigheid",
    ["Soort", "Van", "Tot en met", "Uren per dag", "Opmerking"],
    availabilityRows
  )}

  ${tableSection(
    "Documenten",
    ["Datum", "Type", "Bestandsnaam", "Omschrijving", "Relatie"],
    documentRows
  )}
</body>
</html>`);
    win.document.close();
    win.focus();

    let printStarted = false;
    let fallbackTimer = null;

    const doPrint = () => {
      if (printStarted) return;
      printStarted = true;

      if (fallbackTimer) {
        clearTimeout(fallbackTimer);
        fallbackTimer = null;
      }

      setTimeout(() => {
        try { win.print(); } catch {}
      }, 250);
    };

    const images = Array.from(win.document.images || []);
    if (!images.length || images.every((image) => image.complete)) {
      doPrint();
      return;
    }

    let remaining = images.length;
    const imageDone = () => {
      remaining -= 1;
      if (remaining <= 0) doPrint();
    };

    images.forEach((image) => {
      if (image.complete) imageDone();
      else {
        image.onload = imageDone;
        image.onerror = imageDone;
      }
    });

    fallbackTimer = setTimeout(doPrint, 1500);
  }

  async function openDocument(row) {
    if (!row?.id || !row?.storage_path) return;
    try {
      const response = await fetch(`/api/documents/${encodeURIComponent(row.id)}/open`, { headers: buildHeaders() });
      if (!response.ok) throw new Error("Document openen mislukt.");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { alert(err?.message || "Document openen mislukt."); }
  }

  const toolbarButton = { padding: "8px 11px", border: "1px solid #d9d9d9", borderRadius: 5, background: "white", cursor: "pointer", whiteSpace: "nowrap" };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 100000, background: "rgba(0,0,0,0.42)", padding: isMobile ? 7 : 18, boxSizing: "border-box", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "min(1260px, 100%)", height: isMobile ? "96vh" : "92vh", background: "#fff", borderRadius: 12, boxShadow: "0 22px 70px rgba(0,0,0,0.32)", overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "minmax(0, 1fr) auto", gap: 10, alignItems: "center", padding: isMobile ? 10 : 14, borderBottom: "1px solid #dfe5ea", background: "#f8fafc" }}>
          <div style={{ minWidth: 0 }}>
            <h2 style={{ margin: 0, color: "#1f4e79" }}>Dossier Personeel <span style={{ fontSize: 12, opacity: 0.55 }}>{VERSION}</span></h2>
            <div style={{ marginTop: 3, fontSize: 14, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr 1fr" : "auto auto auto", gap: 7 }}>
            <button type="button" onClick={loadDossier} style={toolbarButton}>🔄 Vernieuw</button>
            <button type="button" onClick={printDossier} style={toolbarButton}>🖨 Printen</button>
            <button type="button" onClick={onClose} style={toolbarButton}>Sluiten</button>
          </div>
        </div>

        <div id="staffiq-personnel-dossier-print" style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: isMobile ? 9 : 14, background: "#f5f7f9" }}>
          {loading && <div>Dossier laden…</div>}
          {error && <div style={{ padding: 12, border: "1px solid #f0b7b7", background: "#fff0f0", color: "#9b1c1c", borderRadius: 7 }}>{error}</div>}
          {!loading && !error && <div style={{ display: "grid", gap: 12 }}>
            <Section title="Algemene gegevens" count={1} initiallyOpen onOpenAll={onClose} openAllLabel="Sluiten">
              <div
                className="person-card-grid"
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "0.95fr 0.95fr 0.95fr 170px",
                  gap: isMobile ? 16 : 18,
                  alignItems: "start",
                  fontSize: 13,
                }}
              >
                <div>
                  <h3 style={{ margin: "0 0 8px", color: "#333" }}>Naam / privé</h3>
                  {[
                    ["Voornaam", person.first_name],
                    ["Voorletters", person.initials],
                    ["Tussenvoegsel", person.name_prefix],
                    ["Achternaam", person.last_name],
                    ["Zoeknaam", person.search_name || title],
                    ["Geslacht", person.gender],
                    ["Geboortedatum", nlDate(person.birth_date)],
                  ].map(([label, value]) => (
                    <div key={label} style={{ display: "grid", gridTemplateColumns: "110px minmax(0,1fr)", gap: 8, padding: "5px 2px", borderBottom: "1px solid #eceff2", lineHeight: 1.35 }}>
                      <div style={{ fontWeight: 700, color: "#444" }}>{label}</div>
                      <div style={{ overflowWrap: "anywhere" }}>{safeTrim(value) || "-"}</div>
                    </div>
                  ))}
                </div>

                <div>
                  <h3 style={{ margin: "0 0 8px", color: "#333" }}>HRM</h3>
                  {[
                    ["Status", person.status],
                    ["Functie", person.job_functions],
                    ["Bedrijfscode", person.company_code_name || person.company_code],
                    ["Nationaliteit", person.nationality],
                    ["Rijbewijs", person.driving_license],
                    ["Eigen vervoer", person.own_transport === true ? "Ja" : "Nee"],
                    ["In dienst", nlDate(person.employment_start_date || person.start_date)],
                    ["Uit dienst", nlDate(person.employment_end_date || person.end_date)],
                    ["Actief", person.active === false ? "Nee" : "Ja"],
                  ].map(([label, value]) => (
                    <div key={label} style={{ display: "grid", gridTemplateColumns: "110px minmax(0,1fr)", gap: 8, padding: "5px 2px", borderBottom: "1px solid #eceff2", lineHeight: 1.35 }}>
                      <div style={{ fontWeight: 700, color: "#444" }}>{label}</div>
                      <div style={{ overflowWrap: "anywhere" }}>{safeTrim(value) || "-"}</div>
                    </div>
                  ))}
                </div>

                <div>
                  <h3 style={{ margin: "0 0 8px", color: "#333" }}>Bezoekadres</h3>
                  {[
                    ["Straat", person.visit_street],
                    ["Huisnummer", person.visit_house_number],
                    ["Toevoeging", person.visit_addition],
                    ["Postcode", person.visit_postcode],
                    ["Plaats", person.visit_city],
                    ["Provincie", person.visit_province],
                    ["Regio", person.visit_region || person.region],
                    ["Land", person.visit_country],
                  ].map(([label, value]) => (
                    <div key={label} style={{ display: "grid", gridTemplateColumns: "105px minmax(0,1fr)", gap: 8, padding: "5px 2px", borderBottom: "1px solid #eceff2", lineHeight: 1.35 }}>
                      <div style={{ fontWeight: 700, color: "#444" }}>{label}</div>
                      <div style={{ overflowWrap: "anywhere" }}>{safeTrim(value) || "-"}</div>
                    </div>
                  ))}
                </div>

                <div>
                  <div style={{ display: "flex", justifyContent: isMobile ? "flex-start" : "center", marginBottom: 8 }}>
                    {person.profile_photo_path ? (
                      <img
                        src={`/api/personnel/${encodeURIComponent(personnelId)}/photo`}
                        alt="Pasfoto"
                        style={{ width: 128, height: 166, objectFit: "cover", display: "block", border: "1px solid #d9d9d9", borderRadius: 8, background: "#f7f7f7" }}
                      />
                    ) : (
                      <div style={{ width: 128, height: 166, border: "1px solid #d9d9d9", borderRadius: 8, background: "#f7f7f7", display: "flex", alignItems: "center", justifyContent: "center", color: "#777" }}>
                        Pasfoto
                      </div>
                    )}
                  </div>

                  {[
                    ["Telefoon", person.phone, "phone"],
                    ["Mobiel", person.mobile, "mobile"],
                    ["E-mail", person.email, "email"],
                    ["Website", person.website, ""],
                  ].map(([label, value, type]) => (
                    <div key={label} style={{ padding: "5px 2px", borderBottom: "1px solid #eceff2", lineHeight: 1.35 }}>
                      <div style={{ fontWeight: 700, color: "#444", marginBottom: 2 }}>{label}</div>
                      <div style={{ overflowWrap: "anywhere" }}>{type ? <CommunicationValue type={type} value={value}/> : safeTrim(value) || "-"}</div>
                    </div>
                  ))}
                </div>
              </div>

              {safeTrim(person.remarks) && (
                <div style={{ marginTop: 12, whiteSpace: "pre-wrap", borderTop: "1px solid #eceff2", paddingTop: 10 }}>
                  <b>Opmerking</b><br/>{person.remarks}
                </div>
              )}
            </Section>

            <Section title="Matchinggegevens" count={matching.length} initiallyOpen>
              {!matching.length ? <EmptyText/> : <TableWrap minWidth={850}><table style={tableStyle}><thead><tr><th style={thStyle}>Soort matching</th><th style={thStyle}>Omschrijving</th><th style={thStyle}>Niveau</th><th style={thStyle}>Periode</th><th style={thStyle}>Diploma</th><th style={thStyle}>Werkgever</th></tr></thead><tbody>{matching.map((row) => <tr key={row.id}><td style={tdStyle}>{row.category || row.type || row.kind || "-"}</td><td style={tdStyle}>{row.name || row.description || "-"}</td><td style={tdStyle}>{row.level || "-"}</td><td style={tdStyle}>{[nlDate(row.date_from), nlDate(row.date_to)].filter(Boolean).join(" t/m ") || "-"}</td><td style={tdStyle}>{row.diploma === true ? "Ja" : "Nee"}</td><td style={tdStyle}>{row.employer || "-"}</td></tr>)}</tbody></table></TableWrap>}
            </Section>

            <Section title="Notities / Taken" count={notesTasks.length} initiallyOpen onOpenAll={() => openFiltered("/notities-taken", personnelId)} openAllLabel="Alle notities/taken">
              {!notesTasks.length ? <EmptyText/> : <TableWrap minWidth={900}><table style={tableStyle}><thead><tr><th style={thStyle}>Soort</th><th style={thStyle}>Status</th><th style={thStyle}>Vervaldatum</th><th style={thStyle}>Titel</th><th style={thStyle}>Relatie</th><th style={thStyle}>Uitvoerder</th><th style={thStyle}>Openen</th></tr></thead><tbody>{notesTasks.map((row) => <tr key={row.id}><td style={tdStyle}>{noteType(row)}</td><td style={tdStyle}>{noteStatus(row)}</td><td style={tdStyle}>{nlDate(row.due_date) || "-"} {safeTrim(row.due_time).slice(0,5)}</td><td style={tdStyle}>{row.subject || row.title || "-"}</td><td style={tdStyle}>{row.organisation_name || "-"}</td><td style={tdStyle}>{row.assigned_user_name || "-"}</td><td style={tdStyle}><button style={linkButtonStyle} onClick={() => storeAndOpen("staffiq_reopen_note_task_id", row.id, `/notities-taken?personnel_id=${encodeURIComponent(personnelId)}`)}>Open</button></td></tr>)}</tbody></table></TableWrap>}
            </Section>

            <Section title="Plaatsingen" count={placements.length} initiallyOpen onOpenAll={() => openFiltered("/plaatsingen", personnelId)} openAllLabel="Alle plaatsingen">
              {!placements.length ? <EmptyText/> : <TableWrap minWidth={1050}><table style={tableStyle}><thead><tr><th style={thStyle}>Datum</th><th style={thStyle}>Nummer</th><th style={thStyle}>Relatie</th><th style={thStyle}>Functie</th><th style={thStyle}>Periode</th><th style={thStyle}>Uren/week</th><th style={thStyle}>Status</th><th style={thStyle}>Kandidaat</th><th style={thStyle}>Klant</th><th style={thStyle}>Openen</th></tr></thead><tbody>{placements.map((row) => <tr key={row.id}><td style={tdStyle}>{nlDate(row.placement_date) || "-"}</td><td style={tdStyle}>{row.placement_number || row.id}</td><td style={tdStyle}>{row.organisation_name || "-"}</td><td style={tdStyle}>{row.job_title || "-"}</td><td style={tdStyle}>{[nlDate(row.start_date), nlDate(row.end_date)].filter(Boolean).join(" t/m ") || "-"}</td><td style={tdStyle}>{decimalNl(row.hours_per_week)}</td><td style={tdStyle}>{row.status || "-"}</td><td style={tdStyle}>{row.candidate_status || "-"}</td><td style={tdStyle}>{row.customer_status || "-"}</td><td style={tdStyle}><button style={linkButtonStyle} onClick={() => storeAndOpen("staffiq_reopen_placement_id", row.id, `/plaatsingen?personnel_id=${encodeURIComponent(personnelId)}`)}>Open</button></td></tr>)}</tbody></table></TableWrap>}
            </Section>

            <Section title="Beschikbaarheid / Afwezigheid" count={availability.length} initiallyOpen onOpenAll={() => openFiltered("/beschikbaarheid", personnelId)} openAllLabel="Volledige beschikbaarheid">
              {!availability.length ? <EmptyText/> : <TableWrap minWidth={760}><table style={tableStyle}><thead><tr><th style={thStyle}>Soort</th><th style={thStyle}>Van</th><th style={thStyle}>Tot en met</th><th style={thStyle}>Uren per dag</th><th style={thStyle}>Opmerking</th></tr></thead><tbody>{availability.map((row) => <tr key={row.id}><td style={tdStyle}>{availabilityLabel(row.type)}</td><td style={tdStyle}>{nlDate(row.start_date) || "-"}</td><td style={tdStyle}>{nlDate(row.end_date) || "-"}</td><td style={tdStyle}>{decimalNl(row.hours_per_day)}</td><td style={tdStyle}>{row.note || "-"}</td></tr>)}</tbody></table></TableWrap>}
            </Section>

            <Section title="Documenten" count={documents.length} initiallyOpen onOpenAll={() => openFiltered("/documenten", personnelId)} openAllLabel="Alle documenten">
              {!documents.length ? <EmptyText/> : <TableWrap minWidth={900}><table style={tableStyle}><thead><tr><th style={thStyle}>Datum</th><th style={thStyle}>Type</th><th style={thStyle}>Bestandsnaam</th><th style={thStyle}>Omschrijving</th><th style={thStyle}>Relatie</th><th style={thStyle}>Openen</th></tr></thead><tbody>{documents.map((row) => <tr key={row.id}><td style={tdStyle}>{nlDate(row.document_date) || nlDateTime(row.created_at) || "-"}</td><td style={tdStyle}>{row.document_type || "-"}</td><td style={tdStyle}>{row.file_name || "-"}</td><td style={tdStyle}>{row.description || "-"}</td><td style={tdStyle}>{row.organisation_name || "-"}</td><td style={tdStyle}>{row.storage_path ? <button style={linkButtonStyle} onClick={() => openDocument(row)}>Open</button> : "-"}</td></tr>)}</tbody></table></TableWrap>}
            </Section>
          </div>}
        </div>
      </div>
    </div>
  );
}
